import { themeAtom } from "../../shared/atoms";
import { Bq1GeneralInformationM } from "../models/bq1-general-information.model";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";

export function Bq1Dimensions(
  form: Bq1GeneralInformationM,
  updateForm: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Dimensions</h2>
      <br />
      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* LOA */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Length overall (LOA)</label>
          <InputCell
            disabled={readonly}
            name="lengthOverallLoa"
            value={form.lengthOverallLoa}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("lengthOverallLoaComments")}
            >
              Comments
            </button>
          </label>
          {(form.lengthOverallLoaComments || isCommentsVisible("lengthOverallLoaComments")) && (
            <textarea
              disabled={readonly}
              name="lengthOverallLoaComments"
              value={form.lengthOverallLoaComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* LBP */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Length between perpendiculars (LBP)</label>
          <InputCell
            disabled={readonly}
            name="lengthBetweenPerp"
            value={form.lengthBetweenPerp}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("lengthBetweenPerpComments")}
            >
              Comments
            </button>
          </label>
          {(form.lengthBetweenPerpComments || isCommentsVisible("lengthBetweenPerpComments")) && (
            <textarea
              disabled={readonly}
              name="lengthBetweenPerpComments"
              value={form.lengthBetweenPerpComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Beam (extreme breadth) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Beam (extreme breadth)</label>
          <InputCell
            disabled={readonly}
            name="beamExtreme"
            value={form.beamExtreme}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("beamExtremeComments")}
            >
              Comments
            </button>
          </label>
          {(form.beamExtremeComments || isCommentsVisible("beamExtremeComments")) && (
            <textarea
              disabled={readonly}
              name="beamExtremeComments"
              value={form.beamExtremeComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Moulded depth */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Moulded depth</label>
          <InputCell
            disabled={readonly}
            name="mouldedDepth"
            value={form.mouldedDepth}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("mouldedDepthComments")}
            >
              Comments
            </button>
          </label>
          {(form.mouldedDepthComments || isCommentsVisible("mouldedDepthComments")) && (
            <textarea
              disabled={readonly}
              name="mouldedDepthComments"
              value={form.mouldedDepthComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      <div className="mb-8 grid grid-cols-4 gap-4">
        {/* Keel to masthead (KTM) */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Keel to masthead (KTM)</label>
          <InputCell
            disabled={readonly}
            name="keelToMastheadKtm"
            value={form.keelToMastheadKtm}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("keelToMastheadKtmComments")}
            >
              Comments
            </button>
          </label>
          {(form.keelToMastheadKtmComments || isCommentsVisible("keelToMastheadKtmComments")) && (
            <textarea
              disabled={readonly}
              name="keelToMastheadKtmComments"
              value={form.keelToMastheadKtmComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* KTM collapsed condition */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Keel to masthead (KTM) in collapsed condition (if appl.)</label>
          <InputCell
            disabled={readonly}
            name="keelToMastheadKtmCollapsed"
            value={form.keelToMastheadKtmCollapsed}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("keelToMastheadKtmCollapsedComments")}
            >
              Comments
            </button>
          </label>
          {(form.keelToMastheadKtmCollapsedComments || isCommentsVisible("keelToMastheadKtmCollapsedComments")) && (
            <textarea
              disabled={readonly}
              name="keelToMastheadKtmCollapsedComments"
              value={form.keelToMastheadKtmCollapsedComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Ballast */}
      <h3 className="text-md mb-2 font-semibold">
        waterline distance to top of hatch coamings/cover in <u>ballast condition</u> (cargo holds not flooded) basis
        50% bunkers:
      </h3>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* First */}
        <div className="col-span-1">
          <label className="block font-semibold">#1 hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceBallastNo1"
            value={form.wlDistanceBallastNo1}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceBallastNo1Comments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceBallastNo1Comments || isCommentsVisible("wlDistanceBallastNo1Comments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceBallastNo1Comments"
              value={form.wlDistanceBallastNo1Comments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Mid */}
        <div className="col-span-1">
          <label className="block font-semibold">Midships</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceBallastMidships"
            value={form.wlDistanceBallastMidships}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceBallastMidshipsComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceBallastMidshipsComments || isCommentsVisible("wlDistanceBallastMidshipsComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceBallastMidshipsComments"
              value={form.wlDistanceBallastMidshipsComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Last */}
        <div className="col-span-1">
          <label className="block font-semibold">Last hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceBallastLast"
            value={form.wlDistanceBallastLast}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceBallastLastComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceBallastLastComments || isCommentsVisible("wlDistanceBallastLastComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceBallastLastComments"
              value={form.wlDistanceBallastLastComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Full Ballast */}
      <h3 className="text-md mb-2 font-semibold">
        waterline distance to top of hatch coamings/cover <u>full ballast condition</u> (cargo holds flooded) basis 50%
        bunkers:
      </h3>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* First */}
        <div className="col-span-1">
          <label className="block font-semibold">#1 hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceFullBallastNo1"
            value={form.wlDistanceFullBallastNo1}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceFullBallastNo1Comments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceFullBallastNo1Comments || isCommentsVisible("wlDistanceFullBallastNo1Comments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceFullBallastNo1Comments"
              value={form.wlDistanceFullBallastNo1Comments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Mid */}
        <div className="col-span-1">
          <label className="block font-semibold">Midships</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceFullBallastMidships"
            value={form.wlDistanceFullBallastMidships}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceFullBallastMidshipsComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceFullBallastMidshipsComments ||
            isCommentsVisible("wlDistanceFullBallastMidshipsComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceFullBallastMidshipsComments"
              value={form.wlDistanceFullBallastMidshipsComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Last */}
        <div className="col-span-1">
          <label className="block font-semibold">Last hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceFullBallastLast"
            value={form.wlDistanceFullBallastLast}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceFullBallastLastComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceFullBallastLastComments || isCommentsVisible("wlDistanceFullBallastLastComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceFullBallastLastComments"
              value={form.wlDistanceFullBallastLastComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Light */}
      <h3 className="text-md mb-2 font-semibold">
        waterline distance to top of hatch coamings/cover <u>light condition</u> basis 50% bunkers:
      </h3>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* First */}
        <div className="col-span-1">
          <label className="block font-semibold">#1 hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLightNo1"
            value={form.wlDistanceLightNo1}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLightNo1Comments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLightNo1Comments || isCommentsVisible("wlDistanceLightNo1Comments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLightNo1Comments"
              value={form.wlDistanceLightNo1Comments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Mid */}
        <div className="col-span-1">
          <label className="block font-semibold">Midships</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLightMidships"
            value={form.wlDistanceLightMidships}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLightMidshipsComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLightMidshipsComments || isCommentsVisible("wlDistanceLightMidshipsComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLightMidshipsComments"
              value={form.wlDistanceLightMidshipsComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Last */}
        <div className="col-span-1">
          <label className="block font-semibold">Last hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLightLast"
            value={form.wlDistanceLightLast}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLightLastComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLightLastComments || isCommentsVisible("wlDistanceLightLastComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLightLastComments"
              value={form.wlDistanceLightLastComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Laden */}
      <h3 className="text-md mb-2 font-semibold">
        waterline distance to top of hatch coamings/cover <u>laden condition</u> basis 50% bunkers:
      </h3>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* First */}
        <div className="col-span-1">
          <label className="block font-semibold">#1 hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLadenNo1"
            value={form.wlDistanceLadenNo1}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLadenNo1Comments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLadenNo1Comments || isCommentsVisible("wlDistanceLadenNo1Comments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLadenNo1Comments"
              value={form.wlDistanceLadenNo1Comments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Mid */}
        <div className="col-span-1">
          <label className="block font-semibold">Midships</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLadenMidships"
            value={form.wlDistanceLadenMidships}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLadenMidshipsComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLadenMidshipsComments || isCommentsVisible("wlDistanceLadenMidshipsComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLadenMidshipsComments"
              value={form.wlDistanceLadenMidshipsComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Last */}
        <div className="col-span-1">
          <label className="block font-semibold">Last hatch</label>
          <InputCell
            disabled={readonly}
            name="wlDistanceLadenLast"
            value={form.wlDistanceLadenLast}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("wlDistanceLadenLastComments")}
            >
              Comments
            </button>
          </label>
          {(form.wlDistanceLadenLastComments || isCommentsVisible("wlDistanceLadenLastComments")) && (
            <textarea
              disabled={readonly}
              name="wlDistanceLadenLastComments"
              value={form.wlDistanceLadenLastComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Dist from keel to top of hatch coamings */}
      <h3 className="text-md mb-2 font-semibold">
        Distance from keel to top of hatch coamings (or top of hatch covers if side-rolling hatches):
      </h3>

      <div className="mb-4 grid grid-cols-4 gap-4">
        {/* First */}
        <div className="col-span-1">
          <label className="block font-semibold">#1 hatch</label>
          <InputCell
            disabled={readonly}
            name="distKeelNo1"
            value={form.distKeelNo1}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("distKeelNo1Comments")}
            >
              Comments
            </button>
          </label>
          {(form.distKeelNo1Comments || isCommentsVisible("distKeelNo1Comments")) && (
            <textarea
              disabled={readonly}
              name="distKeelNo1Comments"
              value={form.distKeelNo1Comments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Mid */}
        <div className="col-span-1">
          <label className="block font-semibold">Midships</label>
          <InputCell
            disabled={readonly}
            name="distKeelMidships"
            value={form.distKeelMidships}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("distKeelMidshipsComments")}
            >
              Comments
            </button>
          </label>
          {(form.distKeelMidshipsComments || isCommentsVisible("distKeelMidshipsComments")) && (
            <textarea
              disabled={readonly}
              name="distKeelMidshipsComments"
              value={form.distKeelMidshipsComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Last */}
        <div className="col-span-1">
          <label className="block font-semibold">Last hatch</label>
          <InputCell
            disabled={readonly}
            name="distKeelLast"
            value={form.distKeelLast}
            onChange={updateForm}
            trailingText="meters"
            inputType="number"
          />
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("distKeelLastComments")}
            >
              Comments
            </button>
          </label>
          {(form.distKeelLastComments || isCommentsVisible("distKeelLastComments")) && (
            <textarea
              disabled={readonly}
              name="distKeelLastComments"
              value={form.distKeelLastComments}
              onChange={updateForm}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Single/double seals in hatch coaming */}
      <div className="mb-8 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            State if hatches fitted with single or double seals in hatch coaming:
          </label>
          <select
            disabled={readonly}
            name="hatchesSingleOrDoubleSeals"
            value={form.hatchesSingleOrDoubleSeals}
            onChange={updateForm}
            className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
          >
            <option value=""></option>
            <option value="Single">Single</option>
            <option value="Double">Double</option>
          </select>
        </div>
      </div>
    </div>
  );
}
